import { useState } from 'react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog'
import { Button } from '../../components/ui/button'

interface Props {
  trigger: React.ReactNode
  title: string
  description: string
  actionLabel: string
  dismissLabel?: string
  destructive?: boolean
  /** bench-SWITCH-SAYS-WHAT-MOVES.CB-E §1④ -- fired alongside every internal setOpen. */
  onOpenChange?: (open: boolean) => void
  onConfirm: () => void | Promise<void>
}

// Guarded destructive confirm — used for Cancel (irreversible) and Amend's not-yet-available
// notice. Forward-advance intentionally does NOT use this (one-tap + undo toast instead).
// Omit dismissLabel for a single-button informational dialog (e.g. the Amend notice).
export function ConfirmDialog({ trigger, title, description, actionLabel, dismissLabel, destructive, onOpenChange, onConfirm }: Props) {
  const [open, setOpenState] = useState(false)
  const [busy, setBusy] = useState(false)

  function setOpen(next: boolean) {
    setOpenState(next)
    onOpenChange?.(next)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          {dismissLabel && (
            <Button variant="outline" size="lg" className="min-h-[48px]" onClick={() => setOpen(false)}>
              {dismissLabel}
            </Button>
          )}
          <Button
            variant={destructive ? 'destructive' : 'default'}
            size="lg"
            className="min-h-[48px]"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              try {
                await onConfirm()
                setOpen(false)
              } finally {
                setBusy(false)
              }
            }}
          >
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
